import { useState, type FormEvent } from "react";
import { Boxes, KeyRound, LogIn, ShieldCheck, UserPlus } from "lucide-react";
import type { AccountProfile } from "../types";

interface AuthGateProps {
  loading: boolean;
  onRegister: (input: { loginId: string; displayName: string; password: string }) => Promise<AccountProfile>;
  onLogin: (input: { loginId: string; password: string }) => Promise<AccountProfile>;
}

type AuthMode = "login" | "register";

export function AuthGate({ loading, onRegister, onLogin }: AuthGateProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [loginId, setLoginId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || loading) return;
    setBusy(true);
    setError("");
    try {
      const normalizedLoginId = loginId.trim();
      if (mode === "register") {
        await onRegister({
          loginId: normalizedLoginId,
          displayName: displayName.trim() || normalizedLoginId,
          password,
        });
      } else {
        await onLogin({ loginId: normalizedLoginId, password });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-card" aria-busy={loading || busy}>
        <div className="auth-brand">
          <div className="brand-mark"><Boxes size={22} /></div>
          <div>
            <span>Roleka By Webrain</span>
            <h1>ログインが必要です</h1>
          </div>
        </div>
        <p className="auth-copy">
          登録またはログインすると、AIチャットを利用できます。
        </p>
        <div className="auth-tabs" role="tablist" aria-label="認証方法">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>
            <LogIn size={16} /> ログイン
          </button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>
            <UserPlus size={16} /> 登録
          </button>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <label className="field-group">
            <span>ログインID</span>
            <input
              value={loginId}
              minLength={3}
              maxLength={32}
              pattern="[A-Za-z0-9_]{3,32}"
              autoComplete="username"
              onChange={(event) => setLoginId(event.target.value)}
              placeholder="webrain_01"
              required
            />
            <small>英数字と _ のみ、3〜32文字</small>
          </label>
          {mode === "register" && (
            <label className="field-group">
              <span>表示名</span>
              <input
                value={displayName}
                maxLength={64}
                autoComplete="nickname"
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Webrain"
              />
            </label>
          )}
          <label className="field-group">
            <span>パスワード</span>
            <input
              type="password"
              value={password}
              minLength={8}
              maxLength={128}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="8文字以上"
              required
            />
          </label>
          {error && <div className="settings-error" role="alert">{error}</div>}
          <button type="submit" className="button button--primary button--full" disabled={busy || loading}>
            <KeyRound size={16} /> {busy || loading ? "確認中…" : mode === "login" ? "ログインして開始" : "登録して開始"}
          </button>
        </form>
        <div className="auth-safe">
          <ShieldCheck size={16} />
          <span>ログイン情報は安全に保護されます。</span>
        </div>
      </section>
    </main>
  );
}
